<template>
  <div>
    <b-modal v-model="modalVisible" :can-cancel="['escape', 'outside', 'button']" :width="600" scroll="keep">
      <div class="modal-content">
        <!-- Show only success message and confirm button if submitted -->
        <template v-if="submitted">
          <p class="success-msg">
            We've emailed your contact with simple instructions to confirm your Profile.<br><br>
            You’ll get a notification in the Teamwrkr app when it’s complete.
          </p>
          <div class="modal-actions">
            <b-button type="is-primary" @click="handleConfirm">Confirm</b-button>
          </div>
        </template>
        <!-- Show original modal contents if not submitted -->
        <template v-else>
          <h2 class="modal-title">
            <i class="mdi mdi-star"></i>
            Get Verified
          </h2>
          <p class="modal-desc">
            Build trust in your skills. Invite someone who knows your work to confirm your Profile.
          </p>
          <p class="modal-subtext">Verification helps show that your experience and skills are accurate and credible.</p>
          <p class="modal-subtext">It's not a performance review, just a quick validation that your profile reflects real-world experience.</p>
          <b-field label="Verification Type">
            <b-select v-model="type" placeholder="Select Verification Type">
              <option disabled value="">Select verification type</option>
              <option disabled value="peer">Peer Review</option>
              <option value="manager">Manager/Supervisor Review</option>
              <option disabled value="client">Client Review (Coming Soon!)</option>
            </b-select>
          </b-field>
          <b-field label="Recipient Email">
            <b-input
              v-model="reviewerEmail"
              type="email"
              :readonly="isReviewerReadOnly"
              placeholder="Enter their email address"
            ></b-input>
          </b-field>
          <b-field label="Add a Personal Message (optional)">
            <b-input v-model="message" type="textarea" placeholder="Include a short note about why you’re requesting this verification."></b-input>
          </b-field>
          <div class="modal-actions">
            <b-button type="is-primary" @click="sendReviewRequest" :loading="loading" :disabled="!reviewerEmail || !type">
              Send Verification Request
            </b-button>
            <b-button type="is-text" @click="closeModal">Cancel</b-button>
          </div>
          <p v-if="error" class="error-msg">
            {{ error }}
          </p>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'GetVerifiedModal',
  props: {
    user: {
      type: Object,
      required: true
    },
    showModal: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      modalVisible: this.showModal,
      type: null,
      reviewerEmail: '',
      message: '',
      loading: false,
      success: false,
      error: '',
      submitted: false,
    };
  },
  watch: {
    showModal(val) {
      this.modalVisible = val;
    },
    modalVisible(val) {
      this.$emit('update:showModal', val);
    },
    type(newType) {
      if (newType === 'manager' && this.user?.manager?.email) {
        this.reviewerEmail = this.user.manager.email;
      } else {
        this.reviewerEmail = '';
      }
    }
  },
  computed: {
    isReviewerReadOnly() {
      return this.type === 'manager';
    }
  },
  methods: {
    closeModal() {
      this.modalVisible = false;
    },
    handleConfirm() {
      this.closeModal();
      this.success = false;
      this.submitted = false;
    },
    async sendReviewRequest() {
      this.loading = true;
      this.success = false;
      this.error = '';
      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/review`,
          {
            type: this.type,
            reviewerEmail: this.reviewerEmail,
            requestMessage: this.message
          },
          {
            headers: {
              Authorization: this.$cookies.get('teamwrkr-token')
            }
          }
        );
        this.success = true;
        this.reviewerEmail = '';
        this.message = '';
        this.submitted = true;
        // Remove setTimeout here!
      } catch (err) {
        this.error = err?.response?.data?.message || 'Could not send review request. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.review-launch-btn {
  background: $color-surface;
  color: $color-primary;
  border: 1px solid $color-primary;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.7rem 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  box-shadow: 0 2px 8px rgba(170, 118, 214, 0.07);
  transition: background 0.18s, color 0.18s, border-color 0.18s;
  margin-bottom: 0.5rem;
  margin-top: 8px;
  margin-bottom: 10px;

  i {
    font-size: 1.5rem;
    color: $color-primary;
    transition: color 0.18s;
  }

  &:hover, &:focus {
    background: $color-primary;
    color: $color-surface;
    border-color: $color-primary;

    i {
      color: $color-warning;
    }
  }
}

.modal-content {
  padding: 1.5rem 1rem;
  background: $color-surface;
  border-radius: 0.75rem;
  color: $color-text;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  box-sizing: border-box;
  max-width: 600px;
  width: 100%;
}

// ::v-deep .modal {
//   max-width: 600px;
//   width: 100%;
//   box-sizing: border-box;
//   overflow-x: hidden;
// }

@media (max-width: 700px) {
  ::v-deep .modal,
  .modal {
    max-width: 100vw !important;
    width: 100vw !important;
    // left: 0 !important;
    // right: 0 !important;
    // margin: 0 !important;
    border-radius: 0.5rem !important;
    overflow-x: hidden !important;
  }
  ::v-deep .modal-content,
  .modal-content {
    padding: 1rem 0rem !important;
    max-width: 100vw !important;
    width: 100vw !important;
    border-radius: 0.5rem !important;
    box-sizing: border-box !important;
    margin: 0 auto !important;
    // overflow-x: hidden !important;
  }
}

body {
  overflow-x: hidden !important;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $color-primary;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;

  i {
    color: $color-warning;
    font-size: 1.7rem;
  }
}

.modal-desc {
  font-size: 1.05rem;
  color: $color-text-muted;
  margin-bottom: 0.5rem;
}

.modal-subtext {
  font-size: 0.92rem;
  color: $color-text-subtle;
  margin-bottom: 0.2rem;
  margin-top: -0.7rem;
  font-style: italic;
  line-height: 1.3;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  justify-content: flex-end;
}

.success-msg {
  color: $color-primary;
  font-weight: 600;
  margin-top: 0.7rem;
}

.error-msg {
  color: $color-accent;
  font-weight: 600;
  margin-top: 0.7rem;
}
</style>
